import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  effect,
} from '@angular/core';
import { ToastService, Toast } from '../../../core/services/toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .toast-container {
      position: fixed;
      bottom: 24px;
      right: 24px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
    }

    .toast-item {
      pointer-events: all;
      display: flex;
      align-items: flex-start;
      gap: 11px;
      min-width: 300px;
      max-width: 420px;
      background: #1a1d22;
      color: #f0f2f5;
      border-radius: 10px;
      padding: 13px 14px 13px 14px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.18);
      position: relative;
      overflow: hidden;
      animation: toast-slide-in 0.22s cubic-bezier(0.2, 0.8, 0.3, 1) forwards;
    }

    .toast-item.dismissing {
      animation: toast-slide-out 0.22s cubic-bezier(0.5, 0, 0.8, 0.3) forwards;
    }

    @keyframes toast-slide-in {
      from { transform: translateX(110%); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }

    @keyframes toast-slide-out {
      from { transform: translateX(0);    opacity: 1; max-height: 80px; margin-bottom: 0; }
      to   { transform: translateX(110%); opacity: 0; max-height: 0;    margin-bottom: -10px; }
    }

    /* Coloured left accent bar */
    .toast-item::before {
      content: '';
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 3px;
      border-radius: 10px 0 0 10px;
    }
    .toast-success::before  { background: #1f8a5b; }
    .toast-error::before    { background: #c0392b; }
    .toast-warning::before  { background: #b87514; }

    /* Bottom progress bar */
    .toast-progress {
      position: absolute;
      bottom: 0; left: 0;
      height: 2px;
      border-radius: 0 0 10px 10px;
      animation: toast-progress linear forwards;
    }
    .toast-success  .toast-progress { background: #1f8a5b; }
    .toast-error    .toast-progress { background: #c0392b; }
    .toast-warning  .toast-progress { background: #b87514; }

    @keyframes toast-progress {
      from { width: 100%; }
      to   { width: 0%; }
    }

    .toast-icon {
      width: 20px; height: 20px;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
      margin-top: 1px;
    }
    .toast-success .toast-icon  { background: rgba(31,138,91,0.2);  color: #34d399; }
    .toast-error   .toast-icon  { background: rgba(192,57,43,0.2);  color: #f87171; }
    .toast-warning .toast-icon  { background: rgba(184,117,20,0.2); color: #fbbf24; }

    .toast-body { flex: 1; min-width: 0; }
    .toast-type-label {
      font-size: 10.5px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      margin-bottom: 2px;
      opacity: 0.65;
    }
    .toast-success  .toast-type-label { color: #34d399; }
    .toast-error    .toast-type-label { color: #f87171; }
    .toast-warning  .toast-type-label { color: #fbbf24; }

    .toast-message {
      font-size: 13px;
      line-height: 1.45;
      color: #e2e8f0;
      font-weight: 500;
    }

    .toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      padding: 2px;
      color: rgba(255,255,255,0.35);
      cursor: pointer;
      border-radius: 4px;
      display: flex; align-items: center; justify-content: center;
      line-height: 1;
      transition: color 0.12s, background 0.12s;
      margin-top: 1px;
    }
    .toast-close:hover { color: rgba(255,255,255,0.8); background: rgba(255,255,255,0.08); }
  `],
  template: `
    <div class="toast-container" aria-live="polite" aria-atomic="false">
      @for (t of visibleToasts(); track t.id) {
        <div
          class="toast-item"
          [class.toast-success]="t.type === 'success'"
          [class.toast-error]="t.type === 'error'"
          [class.toast-warning]="t.type === 'warning'"
          [class.dismissing]="dismissing().has(t.id)"
          role="alert"
        >
          <!-- Icon -->
          <div class="toast-icon">
            @if (t.type === 'success') {
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            } @else if (t.type === 'error') {
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            } @else {
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            }
          </div>

          <!-- Body -->
          <div class="toast-body">
            <div class="toast-type-label">{{ t.type }}</div>
            <div class="toast-message">{{ t.message }}</div>
          </div>

          <!-- Close -->
          <button class="toast-close" (click)="dismiss(t)" aria-label="Dismiss">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>

          <!-- Progress bar -->
          <div
            class="toast-progress"
            [style.animation-duration]="durationFor(t.type)"
          ></div>
        </div>
      }
    </div>
  `,
})
export class ToastComponent {
  private toastService = inject(ToastService);

  readonly visibleToasts = this.toastService.toasts;
  readonly dismissing = signal(new Set<string>());

  dismiss(t: Toast): void {
    this.dismissing.update((s) => {
      const next = new Set(s);
      next.add(t.id);
      return next;
    });
    setTimeout(() => {
      this.toastService.remove(t.id);
      this.dismissing.update((s) => {
        const next = new Set(s);
        next.delete(t.id);
        return next;
      });
    }, 220);
  }

  durationFor(type: Toast['type']): string {
    if (type === 'error') return '5000ms';
    if (type === 'warning') return '4000ms';
    return '3500ms';
  }
}
